import React from 'react';
import { Check, Clock } from 'lucide-react';
import {
  CB_STEPPER_STATUSES,
  CB_STEPPER_LABELS,
  CB_STATUS_FLOW,
  getStatusRegion,
  REGION_COLORS,
  getAgingInfo,
  AGING_STYLES,
} from '../../lib/crossBorder';

/**
 * Horizontal progress stepper for a cross-border load.
 * Shows the trailer's journey from pickup → MX → border → US → delivered,
 * highlighting the current step with its aging time (so operators can
 * instantly tell if a trailer is stuck).
 *
 * Props:
 *  - load: the load object (status, date fields, status_changed_at)
 */
export const CBProgressStepper = ({ load }) => {
  const currentIdx = CB_STEPPER_STATUSES.indexOf(load.status);
  const aging = getAgingInfo(load);

  // Map status → completion date from the load's date fields when possible
  const completionDates = {
    'At the Shipper': load.stops?.find((s) => s.type === 'pickup')?.actual_arrival,
    'In Transit MX': null, // no dedicated field
    'At Border MX': load.border_arrival_date_mx,
    'Drop Off Trailer': null,
    'At Border US': load.border_arrival_date_us,
    'In Transit US': null,
    'Delivered': load.stops?.find((s) => s.type === 'delivery')?.actual_arrival,
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4" data-testid="cb-progress-stepper">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[var(--tms-text)]">CROSS-BORDER PROGRESS</h3>
        {aging && currentIdx >= 0 && currentIdx < CB_STEPPER_STATUSES.length - 1 && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${AGING_STYLES[aging.level]}`}
            data-testid="cb-aging-badge"
            title={`At "${load.status}" for ${aging.label}`}
          >
            <Clock className="w-3 h-3" />
            {aging.level === 'stuck' ? 'STUCK' : 'At this step'} for {aging.label}
          </span>
        )}
      </div>

      <div className="relative flex items-start justify-between gap-1 overflow-x-auto pb-1">
        {CB_STEPPER_STATUSES.map((status, idx) => {
          const region = getStatusRegion(status);
          const colors = REGION_COLORS[region];
          const isDone = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const isFuture = idx > currentIdx;
          const completedAt = completionDates[status];

          return (
            <React.Fragment key={status}>
              {/* Step node + label */}
              <div className="flex flex-col items-center min-w-[80px] flex-shrink-0">
                <div
                  className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isDone
                      ? `${colors.dot} text-white`
                      : isCurrent
                      ? `${colors.bg} ${colors.fg} ring-4 ${colors.ring} font-bold`
                      : 'bg-[var(--tms-hover)] text-[var(--tms-text-muted)] border border-[var(--tms-border)]'
                  }`}
                  data-testid={`cb-step-${idx}`}
                >
                  {isDone ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <span className="text-xs font-semibold">{idx + 1}</span>
                  )}
                  {isCurrent && aging && (
                    <span
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-white ${
                        aging.level === 'stuck' ? 'bg-red-500 animate-pulse' : aging.level === 'warn' ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                    />
                  )}
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-xs font-medium leading-tight ${isFuture ? 'text-[var(--tms-text-muted)]' : 'text-[var(--tms-text)]'}`}>
                    {CB_STEPPER_LABELS[status] || status}
                  </p>
                  {(isDone || isCurrent) && (
                    <p className="text-[10px] mt-0.5 font-mono">
                      {isCurrent && aging ? (
                        <span className={`${
                          aging.level === 'stuck' ? 'text-red-600 font-semibold' :
                          aging.level === 'warn' ? 'text-amber-600' :
                          'text-emerald-600'
                        }`}>
                          {aging.label}
                        </span>
                      ) : completedAt ? (
                        <span className="text-[var(--tms-text-muted)]">{formatDate(completedAt)}</span>
                      ) : isDone ? (
                        <span className="text-[var(--tms-text-muted)]">✓</span>
                      ) : null}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector line between nodes (except after the last) */}
              {idx < CB_STEPPER_STATUSES.length - 1 && (
                <div className="flex-1 h-px bg-[var(--tms-border)] mt-[18px] min-w-[20px] relative">
                  <div
                    className={`absolute inset-0 transition-all ${
                      idx < currentIdx ? REGION_COLORS[getStatusRegion(CB_STEPPER_STATUSES[idx + 1])].dot : 'bg-transparent'
                    }`}
                    style={{ width: idx < currentIdx ? '100%' : '0%' }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Region legend */}
      <div className="mt-4 pt-3 border-t border-[var(--tms-border)] flex items-center justify-center gap-4 text-xs text-[var(--tms-text-muted)]">
        <span className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLORS.mx.dot}`} /> Mexico-side
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLORS.border.dot}`} /> Border yard
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLORS.us.dot}`} /> US-side
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-full ${REGION_COLORS.done.dot}`} /> Delivered
        </span>
      </div>
    </div>
  );
};

export default CBProgressStepper;
